import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  loadAdminPreferences,
  loadAdminAlerts,
  loadAdminThresholds,
  saveAdminPreferences,
  saveAdminAlerts,
  saveAdminThresholds,
  type AdminPreferences,
  type AdminAlertSettings,
  type AdminThresholdSettings,
} from "@/lib/adminSettings";

type SettingsKey = "preferences" | "alerts" | "thresholds";

interface UseAdminSettingsResult {
  preferences: AdminPreferences;
  alerts: AdminAlertSettings;
  thresholds: AdminThresholdSettings;
  isOffline: boolean;
  loading: boolean;
  savePreferences: (next: AdminPreferences) => Promise<void>;
  saveAlerts: (next: AdminAlertSettings) => Promise<void>;
  saveThresholds: (next: AdminThresholdSettings) => Promise<void>;
  refresh: () => Promise<void>;
}

async function fetchFromDb(): Promise<Record<string, unknown> | null> {
  try {
    const { data, error } = await supabase.rpc("get_admin_settings", {
      p_keys: ["preferences", "alerts", "thresholds"],
    });
    if (error) throw error;
    return data && typeof data === "object" ? (data as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

async function saveToDb(key: SettingsKey, value: unknown): Promise<boolean> {
  try {
    const { error } = await supabase.rpc("set_admin_setting", {
      p_key: key,
      p_value: value as never,
    });
    return !error;
  } catch {
    return false;
  }
}

export function useAdminSettings(): UseAdminSettingsResult {
  const [preferences, setPreferences] = useState<AdminPreferences>(loadAdminPreferences);
  const [alerts, setAlerts] = useState<AdminAlertSettings>(loadAdminAlerts);
  const [thresholds, setThresholds] = useState<AdminThresholdSettings>(loadAdminThresholds);
  const [isOffline, setIsOffline] = useState(false);
  const [loading, setLoading] = useState(true);
  const syncedRef = useRef(false);

  const syncFromDb = useCallback(async () => {
    const dbData = await fetchFromDb();
    if (!dbData) {
      setIsOffline(true);
      setLoading(false);
      return;
    }

    setIsOffline(false);
    const hasAnyKey = "preferences" in dbData || "alerts" in dbData || "thresholds" in dbData;

    if (!hasAnyKey && !syncedRef.current) {
      // First load — seed DB with current localStorage values
      const currentPrefs = loadAdminPreferences();
      const currentAlerts = loadAdminAlerts();
      const currentThresholds = loadAdminThresholds();

      const [okPrefs, okAlerts, okThresholds] = await Promise.all([
        saveToDb("preferences", currentPrefs),
        saveToDb("alerts", currentAlerts),
        saveToDb("thresholds", currentThresholds),
      ]);

      if (!okPrefs || !okAlerts || !okThresholds) {
        setIsOffline(true);
      }

      syncedRef.current = okPrefs && okAlerts && okThresholds;
      setLoading(false);
      return;
    }

    // Merge DB values into local state + localStorage;
    // seed any keys missing from DB (partial-seed recovery for P2)
    const missingKeys: SettingsKey[] = [];

    if (dbData.preferences && typeof dbData.preferences === "object") {
      const merged = { ...loadAdminPreferences(), ...(dbData.preferences as Partial<AdminPreferences>) };
      saveAdminPreferences(merged);
      setPreferences(merged);
    } else {
      missingKeys.push("preferences");
    }
    if (dbData.alerts && typeof dbData.alerts === "object") {
      const merged = { ...loadAdminAlerts(), ...(dbData.alerts as Partial<AdminAlertSettings>) };
      saveAdminAlerts(merged);
      setAlerts(merged);
    } else {
      missingKeys.push("alerts");
    }
    if (dbData.thresholds && typeof dbData.thresholds === "object") {
      const merged = { ...loadAdminThresholds(), ...(dbData.thresholds as Partial<AdminThresholdSettings>) };
      saveAdminThresholds(merged);
      setThresholds(merged);
    } else {
      missingKeys.push("thresholds");
    }

    // Re-seed keys that are missing from DB (partial seed recovery)
    if (missingKeys.length > 0) {
      const seedMap: Record<SettingsKey, unknown> = {
        preferences: loadAdminPreferences(),
        alerts: loadAdminAlerts(),
        thresholds: loadAdminThresholds(),
      };
      const results = await Promise.all(missingKeys.map((k) => saveToDb(k, seedMap[k])));
      if (results.some((ok) => !ok)) {
        setIsOffline(true);
      }
    }

    syncedRef.current = true;
    setLoading(false);
  }, []);

  useEffect(() => {
    void syncFromDb();
  }, [syncFromDb]);

  const handleSavePreferences = useCallback(async (next: AdminPreferences) => {
    setPreferences(next);
    saveAdminPreferences(next);
    const ok = await saveToDb("preferences", next);
    setIsOffline(!ok);
  }, []);

  const handleSaveAlerts = useCallback(async (next: AdminAlertSettings) => {
    setAlerts(next);
    saveAdminAlerts(next);
    const ok = await saveToDb("alerts", next);
    setIsOffline(!ok);
  }, []);

  const handleSaveThresholds = useCallback(async (next: AdminThresholdSettings) => {
    setThresholds(next);
    saveAdminThresholds(next);
    const ok = await saveToDb("thresholds", next);
    setIsOffline(!ok);
  }, []);

  return {
    preferences,
    alerts,
    thresholds,
    isOffline,
    loading,
    savePreferences: handleSavePreferences,
    saveAlerts: handleSaveAlerts,
    saveThresholds: handleSaveThresholds,
    refresh: syncFromDb,
  };
}
